import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AtSign, CheckCheck, ChevronRight, Inbox } from 'lucide-react';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { formatDate } from '../../utils/format';
import { getErrorMessage } from '../../lib/apiClient';
import { useAuthStore } from '../../store/authStore';
import { commentKeys, markMentionSeen, markMentionsSeen, myMentions } from './commentsApi';

export default function MentionsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setTenant = useAuthStore((s) => s.setTenant);

  const { data: mentions = [], isLoading } = useQuery({
    queryKey: commentKeys.myMentions(false),
    queryFn: () => myMentions(false),
  });

  const unseen = mentions.filter((m) => !m.seenAt).length;

  // Open a mention: mark it seen, switch to its workspace, and land on the
  // target's location (the comment thread opens from there).
  const openMention = async (m) => {
    try {
      await markMentionSeen(m.id);
      queryClient.invalidateQueries({ queryKey: ['my-mentions'] });
    } catch {
      /* best-effort */
    }
    if (m.orgId && m.workspaceId) {
      setTenant({ orgId: m.orgId, workspaceId: m.workspaceId });
    }
    if (m.targetType === 'folder' && m.targetId) navigate(`/folder/${m.targetId}`);
    else if (m.targetType === 'file' && m.targetId) navigate(`/files/${m.targetId}`);
    else navigate('/files');
  };

  const seenMutation = useMutation({
    mutationFn: markMentionsSeen,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-mentions'] });
      toast.success('Marked all as seen');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not update')),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold text-slate-800">
            <AtSign className="h-5 w-5 text-brand-600" />
            Mentions
          </h1>
          <p className="text-sm text-slate-500">
            {unseen > 0 ? `${unseen} new` : 'You are all caught up'}
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={unseen === 0 || seenMutation.isPending}
          onClick={() => seenMutation.mutate()}
        >
          <CheckCheck className="h-4 w-4" />
          Mark all seen
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-sm">
        {isLoading ? (
          <div className="py-16">
            <Spinner />
          </div>
        ) : mentions.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-slate-400">
            <Inbox className="h-10 w-10" />
            <p className="text-sm">No one has mentioned you yet.</p>
          </div>
        ) : (
          <ul>
            {mentions.map((m) => (
              <li
                key={m.id}
                className={`flex items-center gap-3 border-b border-slate-100 ${
                  m.seenAt ? '' : 'bg-brand-50/40'
                }`}
              >
                <button
                  onClick={() => openMention(m)}
                  className="flex flex-1 items-start gap-3 px-4 py-3 text-left hover:bg-slate-50"
                >
                  {!m.seenAt && (
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-500" />
                  )}
                  <div className={`min-w-0 flex-1 ${m.seenAt ? 'pl-5' : ''}`}>
                    <p className="truncate text-sm text-slate-700">
                      {m.preview ?? <span className="italic text-slate-400">comment removed</span>}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-slate-400">
                      {m.targetName
                        ? `on "${m.targetName}"`
                        : `on a ${m.targetType ?? 'comment'}`}{' '}
                      · {formatDate(m.createdAt)}
                    </p>
                  </div>
                  <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-300" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
